import React from "react";
// UI Frameworks
import { TableCell } from "@mui/material";
import { toast } from "react-toastify";
import CachedIcon from "@mui/icons-material/Cached";
// Common Components
import SimpleTextInput from "src/Components/SimpleTextInput";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import Button from "src/Components/Button";
import ToggleSwitch from "src/Components/ToggleSwitch";
import StaticDropdown from "src/Components/Dropdown/StaticDropdown";
import { Modal } from "src/Components";
// Local Components
import LogsTableModal from "./LogsTableModal";
// Hooks
import { useAppDispatch } from "src/App/hooks";
// Requests
import {
  checkConnection,
  enterCaptcha,
  getNewCaptcha,
  logoutConnection,
  toggleAutoBulkLogin,
  toggleEnableTrade,
  toggleAutoLogin,
  setInstantLogin,
  setInstantBulkLogin,
  logoutConnectionBulk,
} from "src/Requests/connections";
import { apiAddress } from "src/Requests/config";
import {
  disableSahraUserTrade,
  enableSahraUserTrade,
} from "src/Requests/Sahra/sahraUsersRequests";
// Utilities
import { decrementSeconds } from "src/helper";
import { setSahraConnectionsTableFetchDataFlag } from "src/Features/SahraConnections/SahraConnectionsSlice";
import { packetErrorHandler } from "src/Utils/requestUtils";
// Types
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  IAdditional,
  IAdditionalHeaderProps,
} from "src/Tables/TableTemplate/tableTemplate";
import {
  ConnectionStatus,
  IConnectionsReport,
} from "src/Interfaces/connections";

enum Action {
  EnableAutoConnection,
  DisableAutoCunnection,
  EnableTrade,
  DisableTrade,
  None,
}

enum ModalMode {
  Logs,
  Test,
  Closed,
}

const connectionActionErrorHandler = (error: any) => {
  if (error?.response?.status === 403) {
    toast.error("این دسترسی برای شما تعریف نشده است.");
    return;
  }

  packetErrorHandler(error);
};

const ConnectionTableAdditionalHeader: React.FC<IAdditionalHeaderProps> = ({
  selectedRowsIds,
  setSelectedRowsIds,
  onAction,
  isSelectAll,
}) => {
  // states
  const [loading, setLoading] = React.useState(false);
  const [selectedAction, setSelectedAction] = React.useState<Action>(
    Action.None
  );
  const hasSelectedRows = Boolean(selectedRowsIds?.length);
  // hooks
  const dispatch = useAppDispatch();

  const actionOptions: DropdownItem[] = [
    { id: Action.EnableAutoConnection, name: "روشن کردن اتصال اتوماتیک" },
    { id: Action.DisableAutoCunnection, name: "خاموش کردن اتصال اتوماتیک" },
    { id: Action.EnableTrade, name: "روشن کردن قابلیت معامله" },
    { id: Action.DisableTrade, name: "خاموش کردن قابلیت معامله" },
  ];

  const getBulkActionFunction = () => {
    switch (selectedAction) {
      case Action.EnableAutoConnection:
        return toggleAutoBulkLogin;
      case Action.DisableAutoCunnection:
        return toggleAutoBulkLogin;
      case Action.EnableTrade:
        return toggleEnableTrade;
      case Action.DisableTrade:
        return toggleEnableTrade;

      default:
        return null;
    }
  };
  const bulkActionsHandler = () => {
    if (selectedAction === Action.None) return;
    if (!selectedRowsIds || !selectedRowsIds.length) return;
    const actionFunction = getBulkActionFunction();
    if (!actionFunction) return;
    setLoading(true);
    actionFunction({
      userIds: selectedRowsIds,
      enable:
        selectedAction === Action.EnableAutoConnection ||
        selectedAction === Action.EnableTrade,
    })
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  const instantBulkConnectionHandler = () => {
    if (!selectedRowsIds || !selectedRowsIds.length) return;
    setLoading(true);
    setInstantBulkLogin(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
          dispatch(setSahraConnectionsTableFetchDataFlag(true));
        }
        if (!resp.data) toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  const logoutBulkConnectionHandler = () => {
    if (!selectedRowsIds || !selectedRowsIds.length) return;
    setLoading(true);
    logoutConnectionBulk(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
          dispatch(setSahraConnectionsTableFetchDataFlag(true));
        }
        if (!resp.data) toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <>
      <TableCell>
        <span className="!font-black text-black">زمان</span>
      </TableCell>
      <TableCell>
        <span className="!font-black text-black">کپچا</span>
      </TableCell>
      <TableCell></TableCell>
      <TableCell>
        <span className="!font-black text-black">ورود کپچا</span>
      </TableCell>
      <TableCell>
        <div className="flex flex-row gap-1 justify-center items-center w-full">
          <Button
            className="table-btn"
            onClick={instantBulkConnectionHandler}
            text="اتصال فوری"
            variant="contained"
            disabled={loading || !hasSelectedRows}
          />
          <Button
            className="table-btn"
            onClick={logoutBulkConnectionHandler}
            text="قطع"
            variant="outlined"
            disabled={loading || !hasSelectedRows}
          />
        </div>
      </TableCell>
      <TableCell>
        <span className="!font-black text-black">اتصال اتوماتیک</span>
      </TableCell>
      <TableCell>
        <span className="!font-black text-black">قابلیت معامله</span>
      </TableCell>
      <TableCell>
        <span className="!font-black text-black">پیام</span>
      </TableCell>
      <TableCell>
        {loading ? <FullScreenLoading /> : <></>}
        <div className="w-[250px] ">
          <StaticDropdown
            dropdown={{
              name: "",
              placeholder: "عملیات گروهی",
            }}
            onChange={(_, v) => setSelectedAction(v)}
            options={actionOptions}
            value={selectedAction}
          />
        </div>
      </TableCell>
      <TableCell>
        <Button
          text="اعمال"
          onClick={bulkActionsHandler}
          variant="outlined"
          disabled={
            loading ||
            selectedAction === Action.None ||
            !hasSelectedRows
          }
          className="!w-[50px] table-btn m-10"
        />
      </TableCell>
    </>
  );
};

const SahraUsersAdditionalCol: React.FC<{
  row: IConnectionsReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const [modalMode, setModalMode] = React.useState(ModalMode.Closed);
  const [captcha, setCaptcha] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [time, setTime] = React.useState(getTime(row.time));

  const dispatch = useAppDispatch();

  function getTime(time: string) {
    const timeParts = time.split(":");

    return `${timeParts[1]}:${timeParts[2]}:${timeParts[3]}`;
  }

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setTime(decrementSeconds(time));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [time]);

  React.useEffect(() => {
    if (row.estimated) {
      setCaptcha(row.estimated);
    }
  }, [row]);

  const getNewCaptchaHandler = () => {
    setLoading(true);
    getNewCaptcha(row.id)
      .then((resp) => {
        if (resp.data) {
          onAction();
          dispatch(setSahraConnectionsTableFetchDataFlag(true));

          setCaptcha("");
        } else toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  const enterCaptchaHandler = () => {
    setLoading(true);
    enterCaptcha(row.id, captcha)
      .then((resp) => {
        if (resp.data) {
          onAction();
          dispatch(setSahraConnectionsTableFetchDataFlag(true));
          toast.success(resp.msg);
        }
        if (!resp.data) toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  const logoutConnectionHandler = () => {
    setLoading(true);
    logoutConnection(row.id)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
          dispatch(setSahraConnectionsTableFetchDataFlag(true));
        }
        if (!resp.data) toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  const instantConnectionHandler = () => {
    setLoading(true);
    setInstantLogin(row.id)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
          dispatch(setSahraConnectionsTableFetchDataFlag(true));
        }
        if (!resp.data) toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  const checkConncetionHandler = () => {
    setLoading(true);
    checkConnection(row.id)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
          dispatch(setSahraConnectionsTableFetchDataFlag(true));
        }
        if (!resp.data) toast.error(resp.msg);
      })
      .catch(connectionActionErrorHandler)
      .finally(() => setLoading(false));
  };

  const captchaKeyDownHandler = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") enterCaptchaHandler();
  };

  const getCanTradeProperFunction = () => {
    if (row.canTrade) return disableSahraUserTrade(row.id);
    return enableSahraUserTrade(row.id);
  };

  const canTradeChangeHandler = () => {
    getCanTradeProperFunction()
      .then((res) => {
        if (res.data) {
          toast.success(res.msg);
          onAction();
        } else toast.error(res.msg);
      })
      .catch(connectionActionErrorHandler);
  };

  const autoLoginChangeHandler = () => {
    toggleAutoLogin({ userId: row.id, enable: !row.autoLogin })
      .then((res) => {
        if (res.data) {
          toast.success(res.msg);
          onAction();
        } else toast.error(res.msg);
      })
      .catch(connectionActionErrorHandler);
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.Logs:
        return <LogsTableModal userId={row.id} />;

      case ModalMode.Closed:
        return <></>;
    }
  };

  return (
    <>
      <TableCell>
        {row.connectionStatus === ConnectionStatus.Online ? (
          <div>مانده: {time}</div>
        ) : (
          row.time
        )}
      </TableCell>
      <TableCell>
        {row.imageAddress ? (
          <div className="w-16 h-6 mb-px">
            <img src={`${apiAddress()}/v1/captcha/${row.imageAddress}`} />
          </div>
        ) : (
          <div>-</div>
        )}
      </TableCell>
      <TableCell>
        <div onClick={getNewCaptchaHandler}>
          <CachedIcon fontSize="inherit" className="cursor-pointer" />
        </div>
      </TableCell>
      <TableCell>
        {loading ? <FullScreenLoading /> : null}
        {row.imageAddress ? (
          <div className="w-24 h-5 m-1">
            <SimpleTextInput
              placeholder="کپچا"
              name="captcha"
              onChange={(name, value) => setCaptcha(value)}
              value={captcha}
              className="text-xs"
              onKeyDown={captchaKeyDownHandler}
              onMouseDown={() => false}
            />
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <div className="flex flex-row gap-1 w-min">
          <Button
            disabled={row.imageAddress ? false : true}
            className="table-btn"
            onClick={enterCaptchaHandler}
            text="ارسال"
            variant="contained"
          />
          <Button
            className="table-btn"
            onClick={instantConnectionHandler}
            text="اتصال فوری"
            variant="contained"
          />
          {row.connectionStatus !== ConnectionStatus.Offline ? (
            <>
              <Button
                className="table-btn"
                onClick={logoutConnectionHandler}
                text="قطع"
                variant="contained"
              />
            </>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <ToggleSwitch
          checked={row.autoLogin}
          label=""
          name=""
          onChange={autoLoginChangeHandler}
        />
      </TableCell>
      <TableCell>
        <ToggleSwitch
          checked={row.canTrade}
          label=""
          name=""
          onChange={canTradeChangeHandler}
        />
      </TableCell>
      <TableCell>{row.captchaMsg}</TableCell>
      <TableCell>
        <div className="flex justify-center items-center">
          {modalMode !== ModalMode.Closed ? (
            <Modal
              UI={getModalUI()}
              onClose={() => setModalMode(ModalMode.Closed)}
            />
          ) : (
            <></>
          )}
          {loading ? <FullScreenLoading /> : <></>}
          <div className="w-16 self-center">
            <Button
              text="لاگ"
              variant="contained"
              className="table-btn"
              onClick={() => setModalMode(ModalMode.Logs)}
            />
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="w-16">
          <Button
            className="table-btn"
            onClick={checkConncetionHandler}
            text="تست"
            variant="contained"
          />
        </div>
      </TableCell>
    </>
  );
};

export const ConnectionTableAdditional: IAdditional<IConnectionsReport> = {
  getHeader: ConnectionTableAdditionalHeader,
  getRow: SahraUsersAdditionalCol,
};
